import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import {
  BriefCampaignInf,
  BriefCampaignInfs,
} from "@shared/lib/types/briefCampains";
import { BackednResponse } from "@shared/lib/types/response";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { showFailedTaskDatesModal } from "../../ui";
import { BRIEF_CAMPAIGNS_LIST_QUERY_KEY } from "../contsants";
import { CampaignService } from "../services/Campaign.service";
import {
  IUpdateTaskStartEndPayload,
  IUpdateTaskStartEndResponseItem,
} from "../types";

function extractResponseList(data: unknown): IUpdateTaskStartEndResponseItem[] {
  if (Array.isArray(data)) {
    return data as IUpdateTaskStartEndResponseItem[];
  }
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return obj.data as IUpdateTaskStartEndResponseItem[];
    }
    if (obj.data && typeof obj.data === "object") {
      const nested = obj.data as Record<string, unknown>;
      if (Array.isArray(nested.data)) {
        return nested.data as IUpdateTaskStartEndResponseItem[];
      }
    }
  }
  return [];
}

function isItemFailed(item: IUpdateTaskStartEndResponseItem): boolean {
  if (!item) return false;
  const status = (item.status || "").toLowerCase().trim();
  if (status === "failed" || status === "error" || (status !== "success" && Boolean(status))) {
    return true;
  }
  if (Boolean(item.reason) || Boolean(item.reasonType)) {
    return true;
  }
  return false;
}

function isItemSuccess(item: IUpdateTaskStartEndResponseItem): boolean {
  if (!item) return false;
  return (item.status || "").toLowerCase().trim() === "success";
}

export function useUpdateTaskStartEnd(
  briefId: string,
  handleSuccess?: () => void,
  handleError?: () => void
) {
  const queryClient = useQueryClient();

  const {
    mutate: updateTaskStartEnd,
    isLoading: isUpdating,
    isSuccess: isUpdated,
    error,
  } = useMutation<
    IUpdateTaskStartEndPayload,
    BackednResponse<IUpdateTaskStartEndResponseItem[]>
  >({
    mutationFn: (payload: IUpdateTaskStartEndPayload) =>
      CampaignService.updateTaskStartEnd(payload)
        .then((res) => {
          const responseList = extractResponseList(res);
          const failedItems = responseList.filter(isItemFailed);

          if (failedItems.length > 0) {
            const cachedInfluencers: BriefCampaignInf[] = [];
            const queriesData = queryClient.getQueriesData<
              InfiniteData<BackednResponse<BriefCampaignInfs>>
            >({ queryKey: BRIEF_CAMPAIGNS_LIST_QUERY_KEY(briefId) });
            queriesData.forEach(([, queryData]) => {
              queryData?.pages?.forEach((page) => {
                if (page?.data?.data) {
                  cachedInfluencers.push(...page.data.data);
                }
              });
            });

            showFailedTaskDatesModal({
              failedItems,
              totalCount: responseList.length,
              influencers: cachedInfluencers,
            });
          }
          return res;
        })
        .catch(async (err) => {
          try {
            const responseBody = await err.response?.json?.();
            ToastService.error(
              responseBody?.msg || responseBody?.message || "Failed to update stage dates"
            );
          } catch {
            ToastService.error("Failed to update stage dates");
          }
          handleError?.();
          throw err;
        }),
    options: {
      skipInvalidation: true,
    },
    onSuccess: (c, response, payload) => {
      const responseList = extractResponseList(response);

      const failedItems = responseList.filter(isItemFailed);
      const successItems = responseList.filter(isItemSuccess);

      const hasFailed = failedItems.length > 0;

      if (!hasFailed) {
        ToastService.success("Dates updated successfully");
      }

      if (hasFailed && successItems.length === 0) {
        handleError?.();
        c.invalidateQueries({
          queryKey: BRIEF_CAMPAIGNS_LIST_QUERY_KEY(briefId),
        });
        return;
      }

      handleSuccess?.();

      // Map updates strictly by creatorId -> deliverableId -> taskId -> { startAt, endAt }
      const creatorUpdatesMap: Record<
        string,
        Record<string, Record<string, { startAt?: string; endAt?: string }>>
      > = {};

      if (responseList.length > 0) {
        // Build map from ONLY the successful items in the response
        successItems.forEach((item) => {
          const { campaignInfluencerId, deliverableConfigRefId, taskId } = item;
          if (!campaignInfluencerId || !taskId) return;

          const payloadDates =
            payload?.[campaignInfluencerId]?.[deliverableConfigRefId]?.[taskId] ||
            Object.values(payload?.[campaignInfluencerId] || {}).find(
              (taskMap) => taskMap?.[taskId]
            )?.[taskId];

          const startAt = payloadDates?.startAt;
          const endAt = payloadDates?.endAt;

          if (!startAt && !endAt) return;

          if (!creatorUpdatesMap[campaignInfluencerId]) {
            creatorUpdatesMap[campaignInfluencerId] = {};
          }
          const delKey = deliverableConfigRefId || "default";
          if (!creatorUpdatesMap[campaignInfluencerId][delKey]) {
            creatorUpdatesMap[campaignInfluencerId][delKey] = {};
          }
          creatorUpdatesMap[campaignInfluencerId][delKey][taskId] = {
            startAt,
            endAt,
          };
        });
      } else if (payload) {
        Object.entries(payload).forEach(([creatorId, delMap]) => {
          if (!delMap) return;
          if (!creatorUpdatesMap[creatorId]) {
            creatorUpdatesMap[creatorId] = {};
          }
          Object.entries(delMap).forEach(([delId, taskMap]) => {
            if (!taskMap) return;
            if (!creatorUpdatesMap[creatorId][delId]) {
              creatorUpdatesMap[creatorId][delId] = {};
            }
            Object.entries(taskMap).forEach(([taskId, dates]) => {
              if (dates) {
                creatorUpdatesMap[creatorId][delId][taskId] = {
                  startAt: dates.startAt,
                  endAt: dates.endAt,
                };
              }
            });
          });
        });
      }

      // Manually update the query cache with start/end dates ONLY for the specific creator updated
      if (Object.keys(creatorUpdatesMap).length > 0) {
        c.setQueriesData<InfiniteData<BackednResponse<BriefCampaignInfs>>>(
          { queryKey: BRIEF_CAMPAIGNS_LIST_QUERY_KEY(briefId) },
          (old) => {
            if (!old?.pages) return old;

            return {
              ...old,
              pages: old.pages.map((page) => {
                if (!page?.data?.data) return page;

                return {
                  ...page,
                  data: {
                    ...page.data,
                    data: page.data.data.map((inf) => {
                      const deliverableMap =
                        creatorUpdatesMap[inf._id] ||
                        (inf.influencerId ? creatorUpdatesMap[inf.influencerId] : undefined) ||
                        (inf.briefInfluencerId ? creatorUpdatesMap[inf.briefInfluencerId] : undefined);

                      // If this creator was not updated, leave untouched
                      if (!deliverableMap || Object.keys(deliverableMap).length === 0) {
                        return inf;
                      }

                      return {
                        ...inf,
                        deliverables: inf.deliverables?.map((del) => {
                          const taskUpdates =
                            deliverableMap[del._id] ||
                            Object.values(deliverableMap).find((tUpdates) =>
                              del.tasks?.some((t) => tUpdates[t._id])
                            );

                          if (!taskUpdates || !del.tasks) return del;

                          const updatedTasks = del.tasks.map((task) => {
                            const taskUpdate = taskUpdates[task._id];
                            if (!taskUpdate) return task;

                            return {
                              ...task,
                              startAt: taskUpdate.startAt ?? task.startAt,
                              endAt: taskUpdate.endAt ?? task.endAt,
                            };
                          });

                          let updatedLatestTask = del.lastestTask;
                          if (del.lastestTask && taskUpdates[del.lastestTask._id]) {
                            const latestUpdate = taskUpdates[del.lastestTask._id];
                            updatedLatestTask = {
                              ...del.lastestTask,
                              startAt: latestUpdate.startAt ?? del.lastestTask.startAt,
                              endAt: latestUpdate.endAt ?? del.lastestTask.endAt,
                            };
                          }

                          return {
                            ...del,
                            tasks: updatedTasks,
                            lastestTask: updatedLatestTask,
                          };
                        }) ?? [],
                      };
                    }),
                  },
                };
              }),
            };
          }
        );
      }

      // Ensure invalidation still occurs to revalidate background state
      c.invalidateQueries({
        queryKey: BRIEF_CAMPAIGNS_LIST_QUERY_KEY(briefId),
      });
    },
  });

  return {
    updateTaskStartEnd,
    isUpdating,
    isUpdated,
    error,
  };
}
